import { DrawerComponent } from '@shared/components/drawer';
import { Component, computed, HostListener, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { take } from 'rxjs';
import { ButtonComponent } from '@shared/components/button';
import { ModalService } from '@shared/components/modal';
import { FormFieldComponent } from '@shared/components/form-field';
import { SelectComponent } from '@shared/components/select';
import { SpinnerComponent } from '@shared/components/spinner';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import { openSliderAnimation, voirDetailAnimation } from '../../../partage/animations/animations';
import { RolesUtilisateurService } from '../../../partage/components/login/roles-utilisateur.service';
import { ConnectComponent } from './connect/connect.component';
import { DialogCreerClientComponent, DialogCreerClientData } from './dialog-creer-client/dialog-creer-client.component';
import {
  DialogImporterClientComponent,
  DialogImporterClientData,
} from './dialog-importer-client/dialog-importer-client.component';
import { HistoriqueClientComponent } from './historique-client/historique-client.component';
import { HistoriqueResumeComponent } from './historique-resume/historique-resume.component';
import { InformationsClientComponent } from './informations-client/informations-client.component';
import { CreationClient, ImportClient } from './liste-clients.interface';
import { ListeClientsService } from './liste-clients.service';

@Component({
  selector: 'app-liste-clients.route',
  imports: [
    DrawerComponent,
    ButtonComponent,
    FormFieldComponent,
    SelectComponent,
    SpinnerComponent,
    InformationsClientComponent,
    ConnectComponent,
    HistoriqueClientComponent,
    HistoriqueResumeComponent,
  ],
  templateUrl: './liste-clients.route.component.html',
  styleUrl: './liste-clients.route.component.scss',
  // Mêmes animations que le panneau de détail des évènements dans Redditions
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [voirDetailAnimation, openSliderAnimation],
})
export class ListeClientsRouteComponent implements OnInit {
  private listeClientsService = inject(ListeClientsService);
  private modal = inject(ModalService);

  // Import et création touchent aux bases/Key Vault : réservés à Tech et Admin
  accesComplet = inject(RolesUtilisateurService).accesClientsComplet;

  chargementEnCours = this.listeClientsService.chargementEnCours;
  clientSelection = this.listeClientsService.clientSelection;
  filteredTenants = this.listeClientsService.filteredTenants;
  tenantSelectionne = this.listeClientsService.tenantSelectionne;
  environnements = this.listeClientsService.environnements;
  environnementSelectionne = this.listeClientsService.environnementSelectionne;
  historiqueOuvert = this.listeClientsService.historiqueOuvert;

  // `app-select` raisonne en tableau de clés, même en sélection simple : ces
  // projections font la conversion, sans toucher à la forme de l'état du service
  protected optionsEnvironnements = computed<SelectOption[]>(() =>
    this.environnements().map(environnement => ({ value: environnement, label: environnement })),
  );

  protected environnementsSelectionnes = computed<string[]>(() => {
    const courant = this.environnementSelectionne();
    return courant ? [courant] : [];
  });

  // `filteredTenants` ne filtre que si la sélection est une chaîne — un cas que la
  // migration a supprimé, la sélection étant désormais toujours un tenant ou rien.
  // Il rend donc la liste complète, et la recherche est celle du composant
  protected optionsClients = computed<SelectOption[]>(() =>
    this.filteredTenants().map(tenant => ({ value: tenant.identifier, label: tenant.identifier })),
  );

  protected clientsSelectionnes = computed<string[]>(() => {
    const tenant = this.tenantSelectionne();
    return tenant ? [tenant.identifier] : [];
  });

  protected choisirEnvironnement(valeurs: readonly string[]): void {
    const environnement = valeurs[0];
    if (environnement) {
      this.changerEnvironnement(environnement);
    }
  }

  protected choisirClient(valeurs: readonly string[]): void {
    this.listeClientsService.selectionnerClientParIdentifiant(valeurs[0]);
  }

  // Un panneau superposé se ferme à l'échappement : le clic sur le fond ne suffit pas quand
  // le panneau couvre 70 % de l'écran
  @HostListener('document:keydown.escape')
  fermerHistoriqueAuClavier(): void {
    if (this.historiqueOuvert()) {
      this.fermerHistorique();
    }
  }

  fermerHistorique(): void {
    this.listeClientsService.fermerHistorique();
  }

  ngOnInit(): void {
    this.listeClientsService.reinitialiser();
  }

  changerEnvironnement(environnement: string) {
    this.listeClientsService.changerEnvironnement(environnement);
  }

  ouvrirImport() {
    this.listeClientsService
      .recupererInstitutionsImportables$()
      .pipe(take(1))
      .subscribe(importables => {
        const data: DialogImporterClientData = { importables };

        this.modal
          .openForm<ImportClient, DialogImporterClientData>(DialogImporterClientComponent, { data })
          .closed.subscribe(resultat => {
            if (resultat) {
              this.listeClientsService.clientAImporter.set(resultat);
            }
          });
      });
  }

  ouvrirCreation() {
    const data: DialogCreerClientData = {
      basesDeDonnees: this.listeClientsService.connectionStrings(),
      identifiantsExistants: this.listeClientsService.identifiantsExistants(),
    };

    this.modal
      .openForm<CreationClient, DialogCreerClientData>(DialogCreerClientComponent, { data })
      .closed.subscribe(resultat => {
        if (resultat) {
          this.listeClientsService.clientACreer.set(resultat);
        }
      });
  }
}
